import {
  ApiAddItemRequest,
  ApiCabinClass,
  ApiChangeComponentsRequest,
  ApiComponent,
  ApiComponentFromJSON,
  ApiDirection,
  ApiErrorResponse,
  ApiErrorResponseFromJSON,
  ApiFlightItem,
  ApiGeoUnit,
  ApiGeoUnitFromJSON,
  ApiNote,
  ApiPackageCart,
  ApiPackageDetails,
  ApiPackageDetailsFromJSON,
  ApiPackageModelFromJSON,
  ApiPrice,
  ApiRoomContainer,
  ApiTravelDateCandidate,
  ApiTravelDateCandidateFromJSON
} from '@ibe/api';
import { ApiService, decodeObjectBase64, LoggerFactory } from '@ibe/services';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext } from 'react';
import { FlightSearchParams } from '../../../..';
import PackageComponentOptions from '../../../../Models/PackageComponentOptions';
import PackageModelId from '../../../../Models/PackageModelId';
import Keys from '../../../../_Translations/generated/customizer.en.t.json.keys';
import { PackageItemSelection } from '../../../Items/Package/List/PackageListItem';
import { RentalCarSearchParams } from '../../../SubModules/RentalCarSearch/RentalCarSearch';
import ServiceSearchParams from '../../../SubModules/ServiceSearch/ServiceSearchParams';

export class PackageCartStore {
  private logger = LoggerFactory.get('PackageCartStore');

  private api: ApiService;

  _selectedTravelDate: ApiTravelDateCandidate | undefined = undefined;

  _cart: ApiPackageCart | null = null;

  _selectedComponent: ApiComponent | undefined = undefined;

  _selectedComponentOptions: PackageComponentOptions | undefined = undefined;

  error: string | null = null;

  errorResponse: ApiErrorResponse | null = null;

  _isLoading = false;

  _idPackage: string | null = null;

  _idTravelDate: string | null = null;

  _idDetail: string | null = null;

  _rentalCarSearchParams: RentalCarSearchParams | null = null;

  _calculatePrices = true;

  _pendingLoadings = 0;

  constructor() {
    makeObservable(this, {
      _isLoading: observable,
      error: observable,
      errorResponse: observable,
      _cart: observable,
      _selectedComponent: observable,
      _selectedComponentOptions: observable,
      _selectedTravelDate: observable,
      _idPackage: observable,
      _idTravelDate: observable,
      _idDetail: observable,
      _rentalCarSearchParams: observable,
      _pendingLoadings: observable,

      cart: computed,
      hasCart: computed,
      selectedComponent: computed,
      selectedComponentOptions: computed,
      hasSelectedComponent: computed,
      inboundFlightComponent: computed,
      outboundFlightComponent: computed,
      packageSelection: computed,
      components: computed,
      detail: computed,
      idPackage: computed,
      hasPriceBeenUpdated: computed,
      flightSearchParams: computed,
      serviceSearchParams: computed,
      rentalCarSearchParams: computed,
      isLoading: computed,

      setSelection: action,
      setIsLoading: action,
      packageCartStart: action,
      packageCartLoad: action,
      packageCartRemove: action,
      packageCartAddItem: action,
      packageCartRemoveItem: action,
      packageCartReplaceItem: action,
      packageCartSelectItems: action,
      packageCartloadDetails: action,
      setSelectedComponent: action,
      resetSelectedComponent: action,
      resetCart: action,
      changeComponents: action,
      setCalculatePrices: action
    });
  }

  setSelection(idPackage: string, idTravelDate: string, idDetail: string): void {
    this._idPackage = idPackage;
    this._idTravelDate = idTravelDate;
    this._idDetail = idDetail;
  }

  setIsLoading(b: boolean): void {
    if (b) {
      this._pendingLoadings++;
    } else if (this._pendingLoadings > 0) {
      this._pendingLoadings--;
    }
  }

  setCalculatePrices(a: boolean): void {
    this._calculatePrices = a;
  }

  setApi(api: ApiService): void {
    this.api = api;
  }

  get isLoading(): boolean {
    return this._pendingLoadings > 0;
  }

  get idPackage(): string {
    return this._idPackage || '';
  }

  get detail(): ApiPackageDetails | null {
    if (this._cart?.packageModel?.packageDetails?.length === 1) {
      return this._cart.packageModel.packageDetails[0];
    }
    return null;
  }

  get cart(): ApiPackageCart | null {
    return this._cart || null;
  }

  get hasCart(): boolean {
    return !!this._cart;
  }

  resetCart(): void {
    this._cart = null;
  }

  setSelectedComponent(component: ApiComponent | undefined): void {
    this._selectedComponent = component;
  }

  setSelectedComponentOptions(options: PackageComponentOptions | undefined): void {
    this._selectedComponentOptions = options;
  }

  setRentalCarSearchParams(searchParams: RentalCarSearchParams): void {
    this._rentalCarSearchParams = searchParams;
  }

  get selectedComponent(): ApiComponent | undefined {
    return this._selectedComponent || undefined;
  }

  get selectedComponentOptions(): PackageComponentOptions | undefined {
    return this._selectedComponentOptions || undefined;
  }

  get packageSelection(): PackageItemSelection {
    // TODO can we pick the first item???
    return {
      item: this._cart?.packageModel || ApiPackageModelFromJSON({}),
      travelDate: this._cart?.packageModel?.travelDates
        ? this._cart.packageModel.travelDates[0]
        : ApiTravelDateCandidateFromJSON({}),
      detail: this._cart?.packageModel?.packageDetails
        ? this._cart.packageModel.packageDetails[0]
        : ApiPackageDetailsFromJSON({ components: [], priceDetails: [] })
    };
  }

  get components(): ApiComponent[] {
    // TODO can we pick the first item???
    return this._cart?.packageModel.packageDetails
      ? this._cart.packageModel.packageDetails[0].components
      : [ApiComponentFromJSON({})];
  }

  get hasSelectedComponent(): boolean {
    return !!this._selectedComponent;
  }

  resetSelectedComponent(): void {
    this._selectedComponent = undefined;
    this._selectedComponentOptions = undefined;
  }

  static isSearchedType(component: ApiComponent, type: ApiDirection): boolean {
    return !!component.selectableItems.find(item => {
      const flightItem = item as ApiFlightItem;
      return flightItem.direction === type;
    });
  }

  getComponentOfSearchedType(type: ApiDirection): ApiComponent | undefined {
    // TODO really pick first detail????
    if (
      this._cart?.packageModel.packageDetails &&
      this._cart?.packageModel.packageDetails[0].components
    ) {
      return this._cart.packageModel.packageDetails[0].components.find(component =>
        PackageCartStore.isSearchedType(component, type)
      );
    }
    return undefined;
  }

  get outboundFlightComponent(): ApiComponent | undefined {
    return this.getComponentOfSearchedType(ApiDirection.OUTBOUND);
  }

  get inboundFlightComponent(): ApiComponent | undefined {
    return this.getComponentOfSearchedType(ApiDirection.INBOUND);
  }

  hasError(): boolean {
    return !!this.errorResponse || !!this.error;
  }

  async packageAddToBooking(): Promise<void> {
    if (!this._cart) {
      return;
    }
    await this.api.addToBooking(this._cart.id);
  }

  async packageAttemptBooking(): Promise<ApiPackageCart | null> {
    if (!this._cart) {
      return null;
    }
    this.setIsLoading(true);

    let res: ApiPackageCart | null = this._cart;
    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse | null = null;

    try {
      res = await this.api.attemptBooking(this._cart.id);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = Keys.loadingError;
    }
    this.setIsLoading(false);

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
    });
    if (errResponse) {
      return null;
    }
    return res;
  }

  async packageCalculatePrice(
    packageCartId: string,
    componentId: string,
    itemId: string
  ): Promise<ApiPrice | null> {
    this.setIsLoading(true);

    let res: ApiPrice | null = null;

    if (this._cart) {
      try {
        res = await this.api.calculatePrice(packageCartId, componentId, itemId);
      } catch (error) {
        this.logger.warn(error);
      }
    }
    this.setIsLoading(false);

    return res;
  }

  async setSpecialRequest(specialRequestNote: ApiNote): Promise<void> {
    if (!this.cart) {
      return;
    }
    await this.api.setSpecialRequest(this.cart.id, specialRequestNote);
  }

  async packageCartStart(bookingId: string): Promise<void> {
    let res: ApiPackageCart | null = null;
    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse;

    const id = this._idDetail || this._idPackage;
    try {
      this.logger.log('packageCartStart', id, bookingId);
      res = await this.api.start(id || '', bookingId, this._calculatePrices);
      if (!res?.packageModel || !res?.packageModel?.packageDetails) {
        errorMessage = 'emptyPackageComponents';
      }
    } catch (error) {
      this.logger.warn('packageCartStart error:', error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'startPackageCartError';
    }

    runInAction(() => {
      this._cart = res;
      this.error = errorMessage;
      this.errorResponse = errResponse;
    });
  }

  async packageCartLoad(packageId: string): Promise<void> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse;

    try {
      res = await this.api.load(packageId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = Keys.loadingError;
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
      this.logger.log('Finished load package cart');
    });
  }

  async packageCartRemove(packageId: string): Promise<void> {
    this.setIsLoading(true);

    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse;

    try {
      await this.api.remove(packageId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'Api client was not able to remove package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this.error = errorMessage;
      this.logger.log('Finished removing package cart');
    });
  }

  async packageCartAddItem(
    packageCartId: string,
    componentId: string,
    itemId: string
  ): Promise<void> {
    this.setIsLoading(true);

    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse;
    let res: ApiPackageCart | null = null;
    try {
      res = await this.api.addItem(packageCartId, componentId, itemId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to add item to package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
    });
  }

  async packageCartUpdateBooking(
    packageCartId: string,
    attempt = true
  ): Promise<ApiPackageCart | null> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.updateBooking(packageCartId, attempt);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to remove item from package cart';
      return null;
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
    });

    return res;
  }

  async packageCartRemoveItem(
    packageCartId: string,
    componentId: string,
    itemId: string
  ): Promise<void> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.removeItem(packageCartId, componentId, itemId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to remove item from package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
    });
  }

  async packageCartReplaceItem(
    packageCartId: string,
    componentId: string,
    addItemId: string,
    removeItemId: string
  ): Promise<void> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.replaceItem(packageCartId, componentId, addItemId, removeItemId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to replace item in package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
      this.logger.log('Finished replace item in package cart');
    });
  }

  async packageCartSelectItems(
    packageCartId: string,
    componentId: string,
    selectedItemsIds: string[],
    bookingOptions: { [key: string]: ApiAddItemRequest }
  ): Promise<void> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.selectItems(packageCartId, componentId, {
        bookingOptions,
        selectedItemIds: selectedItemsIds
      });
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to select items in package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
      this.logger.log('Finished select items in package cart');
    });
  }

  async packageCartRemoveItems(
    packageCartId: string,
    componentId: string,
    removedItemIds: string[]
  ): Promise<void> {
    this.setIsLoading(true);

    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.removeItems(packageCartId, componentId, {
        removedItemIds: removedItemIds
      });
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to remove items in package cart';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
      this.logger.log('Finished remove items in package cart');
    });
  }

  async packageCartloadDetails(packageCartId: string, componentId: string): Promise<void> {
    let res: ApiPackageCart | null = null;
    let errResponse: ApiErrorResponse;
    let errorMessage: string | null = null;
    try {
      res = await this.api.fetchPackageDetailsForComponent(packageCartId, componentId);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to get package details';
    }

    runInAction(() => {
      this.errorResponse = errResponse;
      this._cart = res;
      this.error = errorMessage;
      this.logger.log('Finished loading details');
    });
  }

  get hasPriceBeenUpdated(): boolean {
    // TODO get price from search result and compare with price from cart
    const previousPrice = null; // where is this?
    const currentPrice = this.detail?.price?.finalPrice;
    return previousPrice && currentPrice ? previousPrice === currentPrice : false;
  }

  get flightSearchParams(): Partial<FlightSearchParams> {
    const packageInformation = decodeObjectBase64<PackageModelId>(
      PackageModelId,
      this.idPackage || ''
    );
    const flightSearchParams: Partial<FlightSearchParams> = {
      origin: this.selectedComponentOptions
        ? this.selectedComponentOptions.departureGeoUnit
        : this.getSelectedFlightOrigin(),
      destination: this.packageSelection.item.geoAssignment?.geoUnit,
      startDate: this.packageSelection.travelDate.startDate,
      endDate: this.packageSelection.travelDate.endDate,
      travellers: {
        cabinClass:
          this.selectedComponentOptions && this.selectedComponentOptions.cabinClass
            ? { code: this.selectedComponentOptions.cabinClass, description: '' }
            : { code: ApiCabinClass.ECONOMY, description: '' },
        adults: packageInformation.roomContainer
          .map(container => container.adults)
          .reduce((prevAd, nextAd) => prevAd + nextAd),
        children: packageInformation.roomContainer
          .map(container => container.children)
          .reduce((prevCh, nextCh) => prevCh + nextCh),
        childrenAges: packageInformation.roomContainer
          .map(container => container.childrenAges)
          .reduce((prevAges, nextAges) => prevAges.concat(nextAges)),
        infants: packageInformation.roomContainer
          .map(container => container.infants)
          .reduce((prevInf, nextInf) => prevInf + nextInf),
        infantAges: []
      }
    };
    return flightSearchParams;
  }

  private getSelectedFlightOrigin(): ApiGeoUnit | undefined {
    if (!this._selectedComponent || !this._selectedComponent.selectedItems.length) {
      return undefined;
    }
    const flightItem = this._selectedComponent.selectedItems[0] as ApiFlightItem;
    if (flightItem.segment.length && flightItem.segment[0].origin) {
      return ApiGeoUnitFromJSON({
        id: '',
        code: flightItem.segment[0].origin.code,
        name: flightItem.segment[0].origin.description,
        typeCode: '',
        children: {}
      });
    }
    return undefined;
  }

  get serviceSearchParams(): Partial<ServiceSearchParams> {
    const packageInformation = decodeObjectBase64<PackageModelId>(
      PackageModelId,
      this.idPackage || ''
    );

    const occupancy: ApiRoomContainer[] = [];

    packageInformation.roomContainer.map(room =>
      occupancy.push({
        adults: room.adults,
        children: room.children,
        childrenAges: room.childrenAges,
        infantsAges: [],
        infants: room.infants
      })
    );

    const serviceSearchParams: Partial<ServiceSearchParams> = {
      startDate: this.packageSelection.travelDate.startDate,
      endDate: this.packageSelection.travelDate.endDate,
      occupancy
    };
    return serviceSearchParams;
  }

  get rentalCarSearchParams(): Partial<RentalCarSearchParams> {
    if (this._rentalCarSearchParams) {
      return this._rentalCarSearchParams;
    }

    return {
      startDate: this.packageSelection.travelDate.startDate,
      endDate: this.packageSelection.travelDate.endDate
    };
  }

  async changeComponents(startDate: string, modifiedComponentId: string): Promise<void> {
    let res: ApiPackageCart | null = null;
    let errorMessage: string | null = null;
    let errResponse: ApiErrorResponse;
    const { components } = this;
    const componentIds = components.map(c => c.id);
    const request: ApiChangeComponentsRequest = {
      startDate,
      modifiedComponentId,
      checkComponentIds: componentIds
    };
    try {
      this.setIsLoading(true);
      res = await this.api.changeComponents(this._cart?.id || '', this._idDetail || '', request);
    } catch (error) {
      this.logger.warn(error);
      errResponse = ApiErrorResponseFromJSON(await (error as Response).json());
      errorMessage = 'API client was not able to change package components';
    }
    this.setIsLoading(false);

    runInAction(() => {
      this._cart = res;
      this.error = errorMessage;
      this.errorResponse = errResponse;
    });
  }
}

const packageCartStore = new PackageCartStore();
export const packageCartStoreContext = createContext(packageCartStore);
